import React, { useState, useEffect } from "react";
import { useDealers } from "hooks/useDealers";
import { useDealersActions } from "hooks/useDealersActions";
import { useDealersSearch } from "hooks/useDealersSearch";

// Modular components
import DealersHeader from "components/dealers/DealersHeader";
import DealersTableView from "components/dealers/DealersTableView";
import DealersCardView from "components/dealers/DealersCardView";
import DealersEmptyState from "components/dealers/DealersEmptyState";
import DealersPagination from "components/dealers/DealersPagination";
import DealersDialogs from "components/dealers/DealersDialogs";
import { PageError } from "components/shared/PageError";
import { searchEmpty } from "utils/emptyCopy";

import { PageContainer } from "styles/components/dealersList.styles";

const DealersList: React.FC = () => {
  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  // State for sorting
  const [sortField] = useState<string>("createdAt");
  const [sortOrder] = useState<string>("DESC");

  // Custom hooks
  const { searchValue, searchInputValue, handleSearchChange } =
    useDealersSearch(setCurrentPage);

  const {
    addDealerDialogVisible,
    deleteDealerDialogVisible,
    enableDealerDialogVisible,
    disableDealerDialogVisible,
    selectedDealer,
    isDeleting,
    isEnabling,
    isDisabling,
    openAddDealerDialog,
    closeAddDealerDialog,
    openEnableDealerDialog,
    closeEnableDealerDialog,
    openDisableDealerDialog,
    closeDisableDealerDialog,
    closeDeleteDealerDialog,
    handleDeleteDealer,
    handleEnableDealer,
    handleDisableDealer,
    handleRowClick,
  } = useDealersActions();

  // Fetch dealers with pagination using our custom hook
  const { data, isLoading, error, refetch } = useDealers(
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    searchValue
  );

  // Update total records when data loads
  useEffect(() => {
    if (data?.pagination) {
      setTotalRecords(data.pagination.totalItems);
    }
  }, [data]);

  // Handle pagination change
  const onPageChange = (event: {
    first: number;
    rows: number;
    page: number;
  }) => {
    setCurrentPage(event.page + 1); // PrimeReact uses 0-based indexing
    setPageSize(event.rows);
  };

  if (error) {
    return (
      <PageContainer>
        <PageError
          title="Error loading dealers"
          message="Please try again later."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  const dealers = data?.response || [];
  const showEmpty = !isLoading && dealers.length === 0;

  return (
    <PageContainer>
      <DealersHeader
        title="Dealers Management"
        onAddDealer={openAddDealerDialog}
        searchValue={searchInputValue}
        onSearchChange={handleSearchChange}
      />

      {showEmpty ? (
        <DealersEmptyState
          isLoading={false}
          onAddDealer={openAddDealerDialog}
          message={
            searchValue
              ? searchEmpty("dealers")
              : "No dealers found. Try adjusting your search criteria or add a new dealer."
          }
        />
      ) : (
        <>
          <DealersTableView
            dealers={dealers}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            onEnableDealer={openEnableDealerDialog}
            onDisableDealer={openDisableDealerDialog}
          />

          <DealersCardView
            dealers={dealers}
            isLoading={isLoading}
            onCardClick={handleRowClick}
            onEnableDealer={openEnableDealerDialog}
            onDisableDealer={openDisableDealerDialog}
          />
        </>
      )}

      {/* Pagination Control */}
      <DealersPagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        showPagination={!!data?.pagination && dealers.length > 0}
      />

      {/* All Dialogs */}
      <DealersDialogs
        addDealerDialogVisible={addDealerDialogVisible}
        onHideAddDealer={closeAddDealerDialog}
        deleteDealerDialogVisible={deleteDealerDialogVisible}
        selectedDealer={selectedDealer}
        isDeleting={isDeleting}
        onHideDeleteDealer={closeDeleteDealerDialog}
        onConfirmDelete={handleDeleteDealer}
        enableDealerDialogVisible={enableDealerDialogVisible}
        isEnabling={isEnabling}
        onHideEnableDealer={closeEnableDealerDialog}
        onConfirmEnable={handleEnableDealer}
        disableDealerDialogVisible={disableDealerDialogVisible}
        isDisabling={isDisabling}
        onHideDisableDealer={closeDisableDealerDialog}
        onConfirmDisable={handleDisableDealer}
      />
    </PageContainer>
  );
};

export default DealersList;
